import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import React, { useCallback } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import { useIsCompareSelected, useComparePkgTrayStore } from "../../../ComparePackageTray";
import { fireTabHandleChange, fireModalOpenCloseEventGA } from "@directv-digital/dds.services.analytics-service";
import { CompareBoxProps } from "../../flip-cards.props";

const styles = {
  container: {
    p: "8px 24px",
    background: "#FFF",
    display: "flex",
    gap: "4px",
    alignItems: "center",
    justifyContent: "flex-end",
    borderRadius: "0 0 24px 24px",
    cursor: "pointer",
    outline: "none"
  },
  iconWrapper: {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    minWidth: "20px",
    minHeight: "20px",
    lineHeight: 0,
    overflow: "visible",
    "&::after": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "16px",
      height: "16px",
      borderRadius: "50%",
      border: "4px solid transparent",
      pointerEvents: "none",
      boxSizing: "content-box"
    }
  }
};

const UnSelectedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M10.0002 1.66666C8.35199 1.66666 6.74059 2.1554 5.37018 3.07108C3.99977 3.98675 2.93179 5.28825 2.30106 6.81097C1.67033 8.33369 1.50534 10.0092 1.82688 11.6257C2.14843 13.2423 2.94199 14.7271 4.10743 15.8925C5.27287 17.058 6.75769 17.8517 8.37419 18.1732C9.9907 18.4948 11.6665 18.3297 13.1892 17.699C14.7119 17.0683 16.0132 16.0002 16.9289 14.6297C17.8446 13.2593 18.3335 11.6482 18.3335 9.99999C18.3335 7.78985 17.4554 5.67024 15.8926 4.10744C14.3298 2.54463 12.2103 1.66666 10.0002 1.66666Z"
      fill="#DFE7F2"
    />
  </svg>
);

const SelectedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M9.99996 1.66666C5.39996 1.66666 1.66663 5.39999 1.66663 9.99999C1.66663 14.6 5.39996 18.3333 9.99996 18.3333C14.6 18.3333 18.3333 14.6 18.3333 9.99999C18.3333 5.39999 14.6 1.66666 9.99996 1.66666ZM14.7444 6.44999L9.69441 14.5889C9.51108 14.8889 9.19441 15.0667 8.86108 15.0667C8.56108 15.0667 8.28329 14.9278 8.08885 14.6833L4.98329 10.7778C4.73329 10.4667 4.76663 10.0055 5.06108 9.73333C5.34441 9.46666 5.81662 9.4611 6.10551 9.72221L8.7444 12.1055L13.4722 5.58888C13.7055 5.26666 14.1944 5.17777 14.5277 5.39999C14.8722 5.63332 14.9722 6.09443 14.7555 6.44999H14.7444Z"
      fill="#006DFF"
    />
  </svg>
);

const CompareBox = (props: CompareBoxProps) => {
  const { compareBox, cardId, cardType, cardName } = props;
  // Narrow subscription: this box only re-renders when ITS OWN selection flips,
  // not when any other card is toggled. The store handle is read separately for
  // imperative actions and never triggers a re-render.
  const isSelected = useIsCompareSelected(cardId);
  const store = useComparePkgTrayStore();

  const compareLabel = compareBox?.content || "Compare";
  const compareAriaLabel = cardName ? `${compareLabel} ${cardName}` : compareLabel;

  const toggle = useCallback(() => {
    if (!cardId || !store) {
      return;
    }
    // Capture pre-toggle state BEFORE updateSelectedTag updates the store, so
    // `isCollapsed` reflects whether this card was just added (collapse the tray)
    // vs removed. updateSelectedTag replaces selectedItems with a new array, so
    // this captured reference stays pre-update.
    const { selectedItems, trayOpen } = store.getState();
    const { updateSelectedTag, updateCollapsedTray, setTrayOpen, triggerElementRef } = store;
    if (triggerElementRef && document.activeElement instanceof HTMLElement) {
      triggerElementRef.current = document.activeElement;
    }
    updateSelectedTag(cardId);
    const isCollapsed = !selectedItems.includes(cardId);
    updateCollapsedTray(isCollapsed);
    setTrayOpen(true);
    fireTabHandleChange({
      event: "trackEvent",
      event_name: "link_click",
      linkName: "genre_pack_checkbox",
      linkPosition: "flip_card",
      linkDestination: "comparison_tray_modal"
    });
    if (!trayOpen) {
      fireModalOpenCloseEventGA({
        dim_name: "open",
        dim_value: "comparison_tray"
      });
    }
  }, [cardId, store]);

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      toggle();
    },
    [toggle]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Space is the standard role="checkbox" key (Enter kept for continuity).
      // Both must stop here — otherwise they bubble to the card and flip it.
      if (e.key !== "Enter" && e.key !== " ") {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      toggle();
    },
    [toggle]
  );

  try {
    return (
      <DtvBox
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="checkbox"
        aria-checked={isSelected}
        aria-label={compareAriaLabel}
        sx={{
          ...styles.container,
          ...(cardType === "back" && {
 px: {
 xs: "8px 16px",
sm: "8px 16px",
md: "8px 24px"
}
}),
          ...(compareBox?.background && { background: compareBox.background }),
          ...(compareBox?.border && { border: compareBox.border }),
          ...(isSelected && { borderTop: "1px solid #5CA3FF" }),
          "&:focus-visible": {
            outline: "none",
            ...(isSelected && { borderTop: "1px solid #5CA3FF" })
          },
          "&:hover .compare-icon-wrapper::after": {
            borderColor: isSelected ? "#DFEDFF" : "#EDF2F7"
          },
          "&:active .compare-icon-wrapper::after": {
            borderColor: isSelected ? "#DFEDFF" : "#EDF2F7"
          },
          "&:focus-visible .compare-icon-wrapper::after": {
            borderColor: "#1FBCF8",
            borderWidth: "2px",
            width: "20px",
            height: "20px"
          }
        }}
      >
        <DtvBox
          component="span"
          className="compare-icon-wrapper"
          sx={styles.iconWrapper}
        >
          {isSelected ? <SelectedIcon /> : <UnSelectedIcon />}
        </DtvBox>
        {compareBox?.content && (
          <DtvText
            content={compareBox.content}
            sx={{
              color: isSelected ? compareBox?.selectedColor || "#0062DB" : compareBox?.color || "#566578"
            }}
            {...compareBox}
          />
        )}
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay error={error} component="CompareBox" />;
  }
};

export default React.memo(CompareBox);
